import { useState, useEffect, useRef, useCallback } from 'react';
import apiClient from '../../utils/api';
import { mergeChatMessages } from '../../utils/chatMessages';
import { listingAgentInitials } from './ListingAgentAvatar';
import ListingPropertyLink from './ListingPropertyLink';
import ReportConversationModal from './ReportConversationModal';

const normalizeEmail = (email) => String(email || '').trim().toLowerCase();

const formatMessageTime = (value) => {
  if (!value) return '';
  const date =
    value?.toDate?.() ? value.toDate()
    : value?._seconds ? new Date(value._seconds * 1000)
    : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const conversationIsAgentSide = (conv, currentUid, userEmail) => {
  if (!conv) return false;
  if (conv.buyerUid && conv.buyerUid === currentUid) return false;
  if (conv.agentUid && conv.agentUid === currentUid) return true;
  if (normalizeEmail(conv.agentEmail) === normalizeEmail(userEmail)) return true;
  return Boolean(conv.buyerUid && conv.buyerUid !== currentUid);
};

const counterpartyLabel = (conv, currentUid, userEmail) => {
  if (conversationIsAgentSide(conv, currentUid, userEmail)) {
    return conv.buyerName || 'Interested buyer';
  }
  return conv.agentName || 'Listing agent';
};

const THREAD_POLL_MS = 15000;
const LIST_POLL_MS = 30000;

const ListingMessagesPanel = ({
  user,
  currentUid,
  addToast,
  initialConversationId = null,
  onUnreadChange,
  onNotifyNewMessages,
  onActiveConversationChange,
  onOpenListing
}) => {
  const [conversations, setConversations] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selectedId, setSelectedId] = useState(initialConversationId);
  const [thread, setThread] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  const threadRef = useRef(null);
  const threadLoadSeqRef = useRef(0);
  const listUnreadRef = useRef(null);

  const userEmail = user?.email || '';

  const loadConversations = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoadingList(true);
    try {
      const res = await apiClient.listListingConversations();
      const list = Array.isArray(res.data) ? res.data : [];
      const totalUnread = list.reduce((sum, row) => sum + (Number(row.unreadCount) || 0), 0);
      setConversations(list);
      if (silent && listUnreadRef.current !== null && totalUnread > listUnreadRef.current) {
        onNotifyNewMessages?.();
      } else {
        onUnreadChange?.();
      }
      listUnreadRef.current = totalUnread;
      return list;
    } catch (err) {
      if (!silent) addToast?.(err.message || 'Could not load your messages.', 'error');
      return [];
    } finally {
      if (!silent) setLoadingList(false);
    }
  }, [addToast, onNotifyNewMessages, onUnreadChange]);

  const loadThread = useCallback(async (conversationId, { silent = false, markRead = !silent } = {}) => {
    if (!conversationId) {
      setThread(null);
      return;
    }
    const seq = ++threadLoadSeqRef.current;
    if (!silent) setLoadingThread(true);
    try {
      const res = await apiClient.getListingMessages(conversationId, { markRead });
      if (seq !== threadLoadSeqRef.current) return;

      const conversation = res.data?.conversation || null;
      const incoming = res.data?.messages || [];
      setThread((prev) => {
        if (prev?.conversation?.id && prev.conversation.id !== conversationId) {
          return { conversation, messages: incoming };
        }
        return {
          conversation,
          messages: mergeChatMessages(prev?.messages || [], incoming),
        };
      });
      if (markRead) {
        setConversations((prev) =>
          prev.map((c) => (c.id === conversationId ? { ...c, unreadCount: 0 } : c))
        );
        onUnreadChange?.();
      }
    } catch (err) {
      if (seq !== threadLoadSeqRef.current) return;
      if (!silent) addToast?.(err.message || 'Could not load this conversation.', 'error');
      if (!silent) setThread(null);
    } finally {
      if (!silent && seq === threadLoadSeqRef.current) setLoadingThread(false);
    }
  }, [addToast, onUnreadChange]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    const id = window.setInterval(() => loadConversations({ silent: true }), LIST_POLL_MS);
    return () => window.clearInterval(id);
  }, [loadConversations]);

  useEffect(() => {
    if (initialConversationId) setSelectedId(initialConversationId);
  }, [initialConversationId]);

  useEffect(() => {
    if (selectedId) loadThread(selectedId);
    else setThread(null);
  }, [selectedId, loadThread]);

  useEffect(() => {
    onActiveConversationChange?.(selectedId || null);
    return () => onActiveConversationChange?.(null);
  }, [selectedId, onActiveConversationChange]);

  useEffect(() => {
    if (!selectedId) return undefined;
    const id = window.setInterval(
      () => loadThread(selectedId, { silent: true, markRead: true }),
      THREAD_POLL_MS
    );
    return () => window.clearInterval(id);
  }, [selectedId, loadThread]);

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [thread?.messages?.length, selectedId]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || !selectedId || sending) return;
    setSending(true);
    threadLoadSeqRef.current += 1;
    try {
      const res = await apiClient.sendListingMessage(selectedId, text);
      const newMsg = res.data;
      setThread((prev) => ({
        ...prev,
        messages: mergeChatMessages(prev?.messages || [], [newMsg]),
      }));
      setDraft('');
      setConversations((prev) =>
        prev.map((c) =>
          c.id === selectedId
            ? {
              ...c,
              lastMessageText: text.slice(0, 240),
              lastMessageAt: new Date().toISOString(),
              unreadCount: 0
            }
            : c
        ).sort((a, b) => {
          const ta = new Date(a.lastMessageAt || 0).getTime();
          const tb = new Date(b.lastMessageAt || 0).getTime();
          return tb - ta;
        })
      );
      onUnreadChange?.();
    } catch (err) {
      addToast?.(err.message || 'Could not send your message.', 'error');
    } finally {
      setSending(false);
    }
  };

  const selectedConv = thread?.conversation || conversations.find((c) => c.id === selectedId);
  const messages = thread?.messages || [];
  const selectedHeadName = selectedConv ? counterpartyLabel(selectedConv, currentUid, userEmail) : '';

  return (
    <div className="listing-messages-layout">
      <aside className={`listing-messages-sidebar${selectedId ? ' listing-messages-sidebar--narrow-mobile' : ''}`}>
        <div className="listing-messages-sidebar-head">
          <h3>Conversations</h3>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={loadConversations}
            disabled={loadingList}
            aria-label="Refresh conversations"
          >
            <i className={`fas fa-sync${loadingList ? ' fa-spin' : ''}`}></i>
          </button>
        </div>
        {loadingList && conversations.length === 0 ? (
          <p className="listing-messages-empty-hint">
            <i className="fas fa-spinner fa-spin"></i> Loading…
          </p>
        ) : conversations.length === 0 ? (
          <div className="listing-messages-empty-hint">
            <i className="fas fa-comments" aria-hidden="true"></i>
            <p>No messages yet. Contact a listing agent from any property page to start a conversation.</p>
          </div>
        ) : (
          <ul className="listing-messages-list">
            {conversations.map((conv) => {
              const label = counterpartyLabel(conv, currentUid, userEmail);
              const active = conv.id === selectedId;
              const unread = Number(conv.unreadCount) || 0;
              return (
                <li key={conv.id}>
                  <button
                    type="button"
                    className={`listing-messages-list-item${active ? ' active' : ''}${unread > 0 ? ' listing-messages-list-item--unread' : ''}`}
                    onClick={() => setSelectedId(conv.id)}
                  >
                    <span className="listing-messages-list-avatar" aria-hidden="true">
                      {listingAgentInitials(label)}
                      {unread > 0 && (
                        <span className="listing-messages-unread-dot" aria-hidden="true" />
                      )}
                    </span>
                    <span className="listing-messages-list-body">
                      <span className="listing-messages-list-top">
                        <strong>{label}</strong>
                        <small>
                          {unread > 0 && (
                            <span className="listing-messages-unread-pill">{unread > 99 ? '99+' : unread}</span>
                          )}
                          {formatMessageTime(conv.lastMessageAt)}
                        </small>
                      </span>
                      <span className="listing-messages-list-property">
                        <ListingPropertyLink
                          propertyId={conv.propertyId}
                          title={conv.propertyTitle}
                          onOpenListing={onOpenListing}
                        />
                      </span>
                      <span className="listing-messages-list-preview">
                        {conv.lastMessageText || 'No messages yet'}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </aside>

      <section className={`listing-messages-thread${!selectedId ? ' listing-messages-thread--empty' : ''}`}>
        {!selectedId ? (
          <div className="listing-messages-thread-placeholder">
            <i className="fas fa-inbox" aria-hidden="true"></i>
            <p>Select a conversation to read and reply</p>
          </div>
        ) : (
          <>
            <div className="listing-messages-thread-head">
              <button
                type="button"
                className="listing-messages-back-btn"
                onClick={() => setSelectedId(null)}
                aria-label="Back to conversations"
              >
                <i className="fas fa-arrow-left"></i>
              </button>
              <div className="listing-messages-thread-head-copy">
                <h3>{selectedHeadName}</h3>
                <p>
                  <ListingPropertyLink
                    propertyId={selectedConv?.propertyId}
                    title={selectedConv?.propertyTitle}
                    onOpenListing={onOpenListing}
                  />
                  {selectedConv?.propertyLocation ? ` · ${selectedConv.propertyLocation}` : ''}
                </p>
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm listing-messages-report-btn"
                onClick={() => setReportOpen(true)}
                title="Report fraud or suspicious activity"
              >
                <i className="fas fa-flag"></i> Report
              </button>
            </div>
            <div className="listing-chat-thread listing-messages-thread-body" ref={threadRef}>
              {loadingThread && messages.length === 0 ? (
                <p className="listing-messages-empty-hint"><i className="fas fa-spinner fa-spin"></i> Loading…</p>
              ) : messages.length === 0 ? (
                <div className="listing-chat-empty">
                  <i className="fas fa-comments" aria-hidden="true"></i>
                  <p>Say hello — your message goes directly to the other party in CoOwn chat.</p>
                </div>
              ) : (
                messages.map((m) => {
                  const mine = m.senderUid === currentUid;
                  return (
                    <div
                      key={m.id}
                      className={`listing-chat-bubble${mine ? ' listing-chat-bubble--mine' : ' listing-chat-bubble--theirs'}`}
                    >
                      {m.text}
                      <small>{mine ? 'You' : (m.senderName || counterpartyLabel(selectedConv, currentUid, userEmail))}</small>
                    </div>
                  );
                })
              )}
            </div>
            <div className="listing-chat-compose listing-messages-compose">
              <textarea
                rows={2}
                placeholder="Write your message…"
                value={draft}
                disabled={sending || loadingThread}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
              />
              <button
                type="button"
                className="listing-chat-send-btn"
                disabled={sending || !draft.trim()}
                onClick={handleSend}
                aria-label="Send message"
              >
                {sending ? <i className="fas fa-spinner fa-spin" aria-hidden="true"></i> : <i className="fas fa-paper-plane" aria-hidden="true"></i>}
              </button>
            </div>
          </>
        )}
      </section>

      {reportOpen && selectedConv && (
        <ReportConversationModal
          conversation={selectedConv}
          addToast={addToast}
          onClose={() => setReportOpen(false)}
        />
      )}
    </div>
  );
};

export default ListingMessagesPanel;
